import { Field, Formik } from "formik";
import React from "react";
import { Form, Button, Collapse } from "react-bootstrap";
import {
  CreatableSelectField,
  formikInput,
  formikSelectInput,
} from "../../../../utils/utils";
import * as Yup from "yup";
import { TagRule } from "../../../../docuclipper/DocuclipperTypes";

type Props = {
  onSubmit: (rule: TagRule) => any;
  rule: TagRule | null;
};

const operatorOptions = [
  { value: "lessThan", label: "is less than" },
  { value: "lessThanInclusive", label: "is less than or equal to" },
  { value: "greaterThan", label: "is greater than" },
  { value: "greaterThanInclusive", label: "is greater than or equal to" },
];

const validationSchema = Yup.object().shape({
  tag: Yup.string().trim().required("Tag is required"),
  keywords: Yup.array().of(Yup.string()),
  negativeKeywords: Yup.array().of(Yup.string()),
  filterByAmount: Yup.boolean(),
  amountOperator: Yup.string().when("filterByAmount", {
    is: true,
    then: Yup.string().required("Pick a comparison"),
  }),
  amountValue: Yup.mixed().when("filterByAmount", {
    is: true,
    then: Yup.number()
      .typeError("Enter a number")
      .required("Amount is required"),
  }),
}).test(
  "at-least-one-criterion",
  "Add at least one keyword, or enable the amount filter — otherwise the rule would match every transaction.",
  (values: any) =>
    (values?.keywords && values.keywords.length > 0) || !!values?.filterByAmount
);

export const TagRuleForm: React.FC<Props> = ({ onSubmit, rule }) => {
  // Existing rules persisted before the form was structured this way may not
  // have `amount` / `description` populated. Defend against partial shapes so
  // the form renders instead of crashing the whole TagRules page.
  const initialAmount = rule?.amount ?? ({ operator: "", value: "" } as any);
  const initialDescription = rule?.description ?? {
    keywords: [],
    negativeKeywords: [],
  };
  const hadAmount = !!initialAmount.operator;
  const handleSubmit = (values, { resetForm }) => {
    const {
      filterByAmount,
      amountOperator,
      amountValue,
      keywords,
      negativeKeywords,
      tag,
    } = values;
    const next: TagRule = {
      amount: filterByAmount
        ? { operator: amountOperator, value: Number(amountValue) }
        : ({ operator: "", value: 0 } as any),
      description: {
        keywords: keywords || [],
        negativeKeywords: negativeKeywords || [],
      },
      tag: tag.trim(),
    };
    onSubmit(next);
    if (!rule) {
      resetForm();
    }
  };

  return (
    <Formik
      enableReinitialize={true}
      initialValues={{
        tag: rule?.tag ?? "",
        keywords: initialDescription.keywords ?? [],
        negativeKeywords: initialDescription.negativeKeywords ?? [],
        filterByAmount: hadAmount,
        amountOperator: hadAmount ? initialAmount.operator : "",
        amountValue: hadAmount ? initialAmount.value : "",
      }}
      validationSchema={validationSchema}
      validateOnMount
      onSubmit={handleSubmit}
    >
      {({ values, errors, setFieldValue, submitForm }) => {
        const formError = (errors as any)[""];
        return (
          <Form data-cy="tag-rule-form" noValidate>
            <Form.Group>
              <Form.Label>
                Tag <span className="text-danger">*</span>
              </Form.Label>
              <Field
                placeholder="e.g. Groceries, Travel, Software"
                type="text"
                name="tag"
                component={formikInput}
                data-cy="tag-rule-tag"
              />
              <Form.Text className="text-muted">
                The label that will be applied to matching transactions.
              </Form.Text>
            </Form.Group>

            <Form.Group data-cy="tag-rule-keywords">
              <Form.Label>Keywords</Form.Label>
              <Field
                name="keywords"
                component={CreatableSelectField}
                initialOptions={[]}
                isMulti
                placeholder="Type a word and press Enter (e.g. starbucks)"
              />
              <Form.Text className="text-muted">
                Match when the transaction description contains any of these.
              </Form.Text>
            </Form.Group>

            <Form.Group data-cy="tag-rule-negative-keywords">
              <Form.Label>Negative keywords <span className="text-muted">(optional)</span></Form.Label>
              <Field
                name="negativeKeywords"
                component={CreatableSelectField}
                initialOptions={[]}
                isMulti
                placeholder="Type a word and press Enter to exclude it"
              />
              <Form.Text className="text-muted">
                Skip transactions whose description contains any of these.
              </Form.Text>
            </Form.Group>

            <Form.Group>
              <Form.Check
                type="checkbox"
                id="tag-rule-filter-by-amount"
                data-cy="tag-rule-filter-by-amount"
                label="Also filter by amount"
                checked={values.filterByAmount}
                onChange={(e) => {
                  const checked = e.target.checked;
                  setFieldValue("filterByAmount", checked);
                  if (!checked) {
                    setFieldValue("amountOperator", "");
                    setFieldValue("amountValue", "");
                  } else if (!values.amountOperator) {
                    setFieldValue("amountOperator", "greaterThanInclusive");
                  }
                }}
              />
            </Form.Group>

            <Collapse in={values.filterByAmount}>
              <div>
                <Form.Group>
                  <Form.Label>Amount</Form.Label>
                  <Field
                    name="amountOperator"
                    component={formikSelectInput}
                    data-cy="tag-rule-amount-operator"
                  >
                    {operatorOptions.map(({ label, value }) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </Field>
                </Form.Group>
                <Form.Group>
                  <Form.Label>Amount value</Form.Label>
                  <Field
                    name="amountValue"
                    type="number"
                    step="0.01"
                    placeholder="e.g. 100.00"
                    component={formikInput}
                    data-cy="tag-rule-amount-value"
                  />
                </Form.Group>
              </div>
            </Collapse>

            {formError && (
              <div className="text-danger mb-2" data-cy="tag-rule-form-error">
                {formError}
              </div>
            )}

            <Button
              variant="primary"
              data-cy="tag-rule-submit"
              onClick={() => submitForm()}
            >
              {rule ? "Save" : "Add Rule"}
            </Button>
          </Form>
        );
      }}
    </Formik>
  );
};
export default TagRuleForm;
